'use client';

import {
  type StyleProps,
  Table as BaseTable,
  type TableProps as BaseTableProps,
  TableContainer as BaseTableContainer,
  type TableContainerProps as BaseTableContainerProps,
  TableBody as BaseTableBody,
  TableBodyCell as BaseTableBodyCell,
  TableBodyRow as BaseTableBodyRow,
  TableHeader as BaseTableHeader,
  TableHeaderCell as BaseTableHeaderCell,
  TableHeaderRow as BaseTableHeaderRow,
  TableEmptyState as BaseTableEmptyState,
} from '@cimpress-ui/react';
import { useMemo } from 'react';
import { flexRender, type RowData } from './tanstack.js';
import type { TableEngine } from './types.js';

export interface TableProps<TData extends RowData>
  extends
    Pick<BaseTableProps, 'id' | 'aria-label' | 'aria-labelledby' | 'aria-describedby' | 'aria-details'>,
    Pick<BaseTableContainerProps, 'UNSAFE_className' | 'UNSAFE_style'>,
    StyleProps {
  /** The data engine created with the `useTableEngine` hook. */
  engine: TableEngine<TData>;
}

/**
 * Renders a table using the provided data engine.
 *
 * See [table usage guidelines](https://ui.cimpress.io/components/table/) and [table building guide](https://ui.cimpress.io/dev-guides/tables/).
 */
export function Table<TData extends RowData>({
  engine,
  UNSAFE_className,
  UNSAFE_style,

  // Style props
  margin,
  marginX,
  marginY,
  marginTop,
  marginRight,
  marginBottom,
  marginLeft,

  ...props
}: TableProps<TData>) {
  const totalColumnCount = useMemo<number | undefined>(() => {
    return engine.getAllColumns().length;
  }, [engine]);

  const columnIdToColumnNumber = useMemo<Record<string, number>>(() => {
    return engine.getAllColumns().reduce<Record<string, number>>((acc, curr, idx) => {
      acc[curr.id] = idx + 1;
      return acc;
    }, {});
  }, [engine]);

  const {
    enableRowSelection,
    enableMultiRowSelection,
    enableSorting,
    enableHiding: enableColumnHiding,
    cimpressUi,
  } = engine.options;

  const isPartialRowData = engine.getPageCount() > 1;
  const headerCount = engine.getHeaderGroups().length;
  const rows = engine.getRowModel().rows;

  return (
    <BaseTableContainer
      UNSAFE_className={UNSAFE_className}
      UNSAFE_style={UNSAFE_style}
      margin={margin}
      marginX={marginX}
      marginY={marginY}
      marginTop={marginTop}
      marginRight={marginRight}
      marginBottom={marginBottom}
      marginLeft={marginLeft}
    >
      <BaseTable
        {...props}
        totalColumnCount={enableColumnHiding ? totalColumnCount : undefined}
        totalRowCount={cimpressUi?.totalRowCount}
        isLoading={cimpressUi?.loadingState?.isLoading}
        rowSelectionMode={enableRowSelection ? (enableMultiRowSelection ? 'multiple' : 'single') : 'none'}
        areAllRowsSelected={enableRowSelection && enableMultiRowSelection ? engine.getIsAllRowsSelected() : undefined}
        areSomeRowsSelected={enableRowSelection && enableMultiRowSelection ? engine.getIsSomeRowsSelected() : undefined}
        onAllRowsSelectionToggle={
          enableRowSelection && enableMultiRowSelection ? engine.toggleAllRowsSelected : undefined
        }
      >
        <BaseTableHeader>
          {engine.getHeaderGroups().map((headerGroup, idx) => (
            <BaseTableHeaderRow key={headerGroup.id} rowNumber={isPartialRowData ? idx + 1 : undefined}>
              {headerGroup.headers.map((header) => {
                const { id: columnId, columnDef } = header.column;
                const sortOrder = header.column.getIsSorted();

                return (
                  <BaseTableHeaderCell
                    key={header.id}
                    columnKey={columnId}
                    columnNumber={enableColumnHiding ? columnIdToColumnNumber[columnId] : undefined}
                    columnWidth={columnDef.width ?? header.column.getSize()}
                    columnContentAlignment={columnDef.contentAlignment}
                    sortOrder={sortOrder ? `${sortOrder}ending` : undefined}
                    onSortOrderToggle={
                      enableSorting && header.column.getCanSort() ? header.column.toggleSorting : undefined
                    }
                    iconStart={columnDef.headerIconStart}
                    iconEnd={columnDef.headerIconEnd}
                  >
                    {header.isPlaceholder ? null : flexRender(columnDef.header, header.getContext())}
                  </BaseTableHeaderCell>
                );
              })}
            </BaseTableHeaderRow>
          ))}
        </BaseTableHeader>

        <BaseTableBody>
          {rows.length === 0 && cimpressUi?.emptyState ? (
            <BaseTableEmptyState {...cimpressUi.emptyState} />
          ) : (
            rows.map((row) => (
              <BaseTableBodyRow
                key={row.id}
                rowNumber={
                  isPartialRowData
                    ? (cimpressUi?.getRowNumber?.(row.original) ?? headerCount + row.index + 1)
                    : undefined
                }
                isSelected={enableRowSelection && row.getIsSelected()}
                onSelectionToggle={enableRowSelection ? row.toggleSelected : undefined}
              >
                {row.getVisibleCells().map((cell) => (
                  <BaseTableBodyCell key={cell.id} columnKey={cell.column.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </BaseTableBodyCell>
                ))}
              </BaseTableBodyRow>
            ))
          )}
        </BaseTableBody>
      </BaseTable>
    </BaseTableContainer>
  );
}

Table.displayName = 'Table';
